import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Grid, Button, InputBase, Alert, IconButton, Tooltip, Box
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { ResetNewPassword } from '../utils/ApiLogin';
import Logo from '../assets/img/LogoItg.png';
import Traductor from '../components/Traductor';
import Base from '../components/BaseLogos/Base';
import {
  Translate as TranslateIcon,
  LockOutlined as LockIcon,
  LockOpen as UnlockIcon
} from '@mui/icons-material';
import '../assets/css/Login.css';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#90caf9' },
    background: { default: '#121212', paper: '#1d1d1d' },
  },
});

const ResetPassword = ({
  translate, idioma,
  SWVersion, handleClick,
  openMenuIdioma, handleCloseIdioma,
  handleLanguageChange
}) => {
  const { token } = useParams();

  const [newInput, setNewInput] = useState('');
  const [repeatInput, setRepeatInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Regex: min 6 chars, at least 1 uppercase, 1 number, 1 symbol
  const complexityRegex = /(?=.*[A-Z])(?=.*\d)(?=.*\W).{6,}/;
  const isComplex = complexityRegex.test(newInput);
  const passwordsMatch = newInput === repeatInput && newInput !== '';
  const canSubmit = isComplex && passwordsMatch;

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const success = await ResetNewPassword({
        password: repeatInput,
        token
      });

      if (success) {
        setSuccessMessage(translate('email_sent_successfully'));
        window.location.href = '/';
      } else {
        setError(translate('error'));
      }
    } catch {
      setError(translate('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Box className="login-form-container">
        <Box className="lang-selector">
          <Tooltip title={translate(idioma)} arrow>
            <IconButton onClick={handleClick}>
              <TranslateIcon style={{ fontSize: '1vw' }}/>
            </IconButton>
          </Tooltip>
          <Traductor
            idioma={idioma}
            openMenuIdioma={openMenuIdioma}
            handleCloseIdioma={handleCloseIdioma}
            handleLanguageChange={handleLanguageChange}
            translate={translate}
          />
        </Box>

        <Box component="form" onSubmit={handleSubmit} className="login-form">
          <img src={Logo} className="logo" alt="Logo ITG" />
          <h2 className="form-title">{translate('recuperar')}</h2>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <label className="label">
                <span className="title">{translate('new_password')}</span>
                <div className="input-wrapper">
                  {showPassword
                    ? <UnlockIcon className="icon" onClick={() => setShowPassword(false)} />
                    : <LockIcon className="icon" onClick={() => setShowPassword(true)} />
                  }
                  <InputBase
                    fullWidth
                    className="input-field"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={translate('password')}
                    value={newInput}
                    onChange={e => setNewInput(e.target.value)}
                    required
                  />
                </div>
              </label>
            </Grid>

            <Grid item xs={12}>
              {!isComplex && newInput !== '' && (
                <Alert severity="warning" sx={{ fontSize: '1vw' }}>
                  {translate('password_requirements') /* define in translations: "Mínimo 6 caracteres, 1 mayúscula, 1 número y 1 símbolo" */}
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <label className="label">
                <span className="title">{translate('rep_new_password')}</span>
                <div className="input-wrapper">
                  {showPassword
                    ? <UnlockIcon className="icon" onClick={() => setShowPassword(false)} />
                    : <LockIcon className="icon" onClick={() => setShowPassword(true)} />
                  }
                  <InputBase
                    fullWidth
                    className="input-field"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={translate('password')}
                    value={repeatInput}
                    onChange={e => setRepeatInput(e.target.value)}
                    required
                  />
                </div>
              </label>
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  fontSize: '1vw',
                  padding: '1.5vh 0',
                  backgroundColor: '#e84e0e',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#d84300' }
                }}
                disabled={loading || !canSubmit}
              >
                {loading ? translate('loading') : translate('enviar_pass')}
              </Button>
            </Grid>

            {!passwordsMatch && repeatInput !== '' && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ fontSize: '1vw' }}>
                  {translate('passwords_do_not_match')}
                </Alert>
              </Grid>
            )}

            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ fontSize: '1vw' }}>{error}</Alert>
              </Grid>
            )}

            {successMessage && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ fontSize: '1vw' }}>{successMessage}</Alert>
              </Grid>
            )}
          </Grid>

          <div className="version">{SWVersion}</div>
        </Box>

        <Base />
      </Box>
    </ThemeProvider>
  );
};

export default ResetPassword;

